import ContactUsForm from "components/ContactUsForm";
import { useEffect, useState } from "react";
import { Button, Modal, ModalBody } from "reactstrap";
import { trackEvent } from "utils/analytics";

const SUPPRESSION_KEY = 'contact-form';
// How far down the page (0-1) the visitor must scroll before the popup
// becomes eligible. Scroll depth is the fallback for touch devices, which
// have no exit-intent signal.
const SCROLL_THRESHOLD = 0.75;

const isSuppressed = () => {
    try {
        return localStorage.getItem(SUPPRESSION_KEY) === 'closed';
    } catch (e) {
        return false;
    }
};

export default function ContactUsRequestPopup({
    headline = "Have questions? Let's talk.",
    subline = "Tell us about your store and we'll help with setup, Enterprise quotes, or anything else.",
    source = 'unknown',
    formSource = 'popup',
    forceOpenSignal = 0,
    disabled = false
}) {
    const [open, setOpen] = useState(false);

    const handleClose = () => {
        try {
            localStorage.setItem(SUPPRESSION_KEY, 'closed');
        } catch (e) {
            // private browsing etc. — the popup simply won't be suppressed
        }
        setOpen(false);
    };

    // Manual open from the footer "Contact Us" link. Bypasses suppression
    // because it reflects explicit intent.
    useEffect(() => {
        if (forceOpenSignal > 0) {
            setOpen(true);
            trackEvent('engagement', 'contact_popup_view', `${source}:manual`);
        }
    }, [forceOpenSignal, source]);

    // Smart triggers: exit intent on desktop, deep scroll everywhere else.
    // Fires at most once — closing the modal suppresses it via localStorage.
    useEffect(() => {
        if (disabled || forceOpenSignal > 0 || isSuppressed()) return;

        let triggered = false;
        const cleanup = () => {
            document.removeEventListener('mouseout', handleMouseOut);
            window.removeEventListener('scroll', handleScroll);
        };
        const trigger = () => {
            if (triggered || isSuppressed()) return;
            triggered = true;
            cleanup();
            setOpen(true);
            trackEvent('engagement', 'contact_popup_view', source);
        };
        const handleMouseOut = (e) => {
            // Pointer leaving the viewport at the top = about to leave.
            if (!e.relatedTarget && e.clientY <= 0) {
                trigger();
            }
        };
        const handleScroll = () => {
            const max = document.documentElement.scrollHeight - window.innerHeight;
            if (max > 0 && (window.scrollY / max) >= SCROLL_THRESHOLD) {
                trigger();
            }
        };

        document.addEventListener('mouseout', handleMouseOut);
        window.addEventListener('scroll', handleScroll, { passive: true });

        return cleanup;
    }, [disabled, forceOpenSignal, source]);

    return (open &&
        <Modal isOpen={open}>
            <div style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid #eee',
                padding: '1rem 1.5rem'
            }}>
                <div>
                    <h5 className="mb-0">{headline}</h5>
                    <small className="text-muted">{subline}</small>
                </div>
                <Button size='sm' onClick={() => handleClose()}>&times;</Button>
            </div>
            <ModalBody>
                <ContactUsForm formSource={formSource} onSubmitSuccess={() => {
                    handleClose()
                }} onCancel={() => handleClose()} />
            </ModalBody>
        </Modal>
    )

}
